import React, { useState } from 'react';

// Components
import { CheckboxField } from '@zola/zola-ui/src/components/Form/CheckboxField';
import { GiftIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Gift';
import { GiftBagIcon } from '@zola/zola-ui/src/components/SvgIconsV3/GiftBag';
import { HotAirBalloonIcon } from '@zola/zola-ui/src/components/SvgIconsV3/HotAirBalloon';
import { CashFundIcon } from '@zola/zola-ui/src/components/SvgIconsV3/CashFund';
import { CircleDollarIcon } from '@zola/zola-ui/src/components/SvgIcons';
import { GiftGroupIcon } from '@zola/zola-ui/src/components/SvgIconsV3/GiftGroup';
import OnboardStep from '@zola/zola-ui/src/components/OnboardV2/OnboardStep';
import OnboardPane from '@zola/zola-ui/src/components/OnboardV2/OnboardPane';
import registryImgPane from 'assets/images/onboardSandbox/RegistryProduct/registry-valuable-product-cropped.jpg';
import registryOverlayImg from 'assets/images/onboardSandbox/RegistryProduct/registry-value-props.png';

import {
  Hed,
  Dek,
  StyledChip,
  Grid,
  ContentContainer,
  IconContainer,
  ValuePropsImg,
} from './RegistryProductStep.styles';

const ICON_SIZE = 24;
const content = [
  {
    icon: <GiftIcon height={ICON_SIZE} width={ICON_SIZE} />,
    hed: 'Gifts from Zola',
    dek: "1000s of top brands. From kitchen essentials to camping gear, we've got everything.",
    href: '/registry/checklist',
  },
  {
    icon: <HotAirBalloonIcon height={ICON_SIZE} width={ICON_SIZE} />,
    hed: 'Experiences',
    dek: 'Indoor skydiving? Sign us up.',
    href: '/shop/category/local-experiences',
  },
  {
    icon: <CashFundIcon height={ICON_SIZE} width={ICON_SIZE} />,
    hed: 'Gift cards',
    dek: 'All your favorites, all right here.',
    href: '/shop/category/gift-cards',
  },
  {
    icon: <CircleDollarIcon height={ICON_SIZE} width={ICON_SIZE} />,
    hed: 'Cash funds',
    dek: 'Zero-fee and low-fee options for anything you want.',
    href: '/shop/honeymoon-cash-funds',
  },
  {
    icon: <GiftBagIcon height={ICON_SIZE} width={ICON_SIZE} />,
    hed: 'Gifts from other stores',
    dek: 'See it, like it, add it.',
    href: '/wedding-registry/add-from-other-stores',
  },
  {
    icon: <GiftGroupIcon height={ICON_SIZE} width={ICON_SIZE} />,
    hed: 'Charities',
    dek: 'Spread the love and give back.',
    href: '/shop/category/charity',
  },
];

type RegistryProductStepProps = {
  selectedHref: string;
  onSelectHref: (href: string) => void;
};

const RegistryProductStep = ({ selectedHref, onSelectHref }: RegistryProductStepProps) => {
  const [checkboxSelected, setCheckboxSelected] = useState(false);

  const handleCheckboxClick = () => {
    setCheckboxSelected(true);
    onSelectHref('/shop/gift-collections/starter-collections');
  };

  const renderOptions = content.map(({ icon, hed, dek, href }) => {
    const handleClick = () => {
      onSelectHref(href);
      if (checkboxSelected) setCheckboxSelected(false);
    };

    const isActive = selectedHref === href;

    return (
      // TODO: Add type as an acceptable prop for Chip in zola-ui (and set default type to button)
      // @ts-expect-error Chip is an HTMLButtonElement, but it's defaulting to submit behavior in this scenario; type="button" ensures it doesn't submit the surrounding form
      <StyledChip type="button" onClick={handleClick} selected={isActive} key={href}>
        <IconContainer>{icon}</IconContainer>
        <ContentContainer>
          <Hed>{hed}</Hed>
          <Dek>{dek}</Dek>
        </ContentContainer>
      </StyledChip>
    );
  });

  return (
    <OnboardStep
      hed="Our registry has it all. What do you want to add first?"
      dek="Choose just one for now. We'll help you add the rest later."
      portalContent={
        <OnboardPane
          imgSrcs={{
            desktop: registryImgPane,
            mobile: registryImgPane,
            tablet: registryImgPane,
            alt: '',
          }}
          hed={<ValuePropsImg src={registryOverlayImg} />}
        />
      }
      isValid={!!selectedHref}
    >
      <Grid>{renderOptions}</Grid>
      <CheckboxField
        id="onboard-not-sure"
        label="Not sure—surprise us!"
        onChange={handleCheckboxClick}
        checked={checkboxSelected}
      />
    </OnboardStep>
  );
};

export default RegistryProductStep;
